import {Component, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Select, Store} from '@ngxs/store';
import {Observable} from 'rxjs/internal/Observable';
import {AuthState} from '../../../shared/states/auth/auth.state';
import {InvitedSignUp, SetPharmacyId, SignUp, ValidateInvitation} from '../../../shared/states/auth/auth.actions';
import {IInvitedSignUp} from '../../interfaces/invited-sign-up.interface';
import {CustomEmailValidator} from '../../../shared/validators/custom-email.validator';
import {StrongPasswordValidator} from '../../../shared/validators/strong-password.validator';
import {take, withLatestFrom} from 'rxjs/operators';
import {ActivatedRoute, Params} from '@angular/router';
import {GetUserPharmacies, SetCurrentPharmacy, SetPricingOption} from 'src/app/shared/states/pharmacy/pharmacy.actions';
import {PharmacyState} from 'src/app/shared/states/pharmacy/pharmacy.state';
import {IPharmacy} from 'src/app/shared/interfaces/pharmacy.interface';
import {BadgeColorsEnum} from '../../../shared/interfaces/BadgeColorsEnum';
import {PricingOption} from 'src/app/shared/interfaces/PricingOption';
import {SignUpPriceEnum} from '../../interfaces/SignUpPriceEnum';
import {IPricingOption} from '../../interfaces/pricing-option.interface';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss', './../../auth.scss'],
})
export class SignUpComponent implements OnInit {
  signUpForm: UntypedFormGroup;
  showForm: boolean;
  showInvitationErrors: boolean;
  userExistsError: boolean;
  invitationExpired: boolean;
  inviteData: any;

  pricingMonthOptions: IPricingOption[] = [
    {
      id: 1,
      header: 'Basic',
      name: '$130/mon.',
      price: 130,
      description: 'Billed monthly.',
      checked: false,
    },
    {
      id: 2,
      header: 'Standard',
      name: '$195/mon.',
      price: 195,
      description: 'Billed monthly.',
      checked: false,
    },
    {
      id: 3,
      header: 'Advanced',
      name: '$233/mon.',
      price: 233,
      description: 'Billed monthly.',
      checked: false,
    },
  ];

  pricingYearOptions: IPricingOption[] = [
    {
      id: 4,
      header: 'Basic',
      name: '$112/mon.',
      price: 112,
      description: 'Billed annually.',
      checked: false,
    },
    {
      id: 5,
      header: 'Standard',
      name: '$167/mon.',
      price: 167,
      description: 'Billed annually.',
      checked: false,
    },
    {
      id: 6,
      header: 'Advanced',
      name: '$200/mon.',
      price: 200,
      description: 'Billed annually.',
      checked: false,
    },
  ];

  currentPricingOption: IPricingOption = {} as IPricingOption;

  BadgeColorsEnum = BadgeColorsEnum;

  currentTab: SignUpPriceEnum = SignUpPriceEnum.Month;
  SignUpPriceEnum = SignUpPriceEnum;

  yearPriceHeader: SafeHtml;

  @ViewChild('firstNameInput') firstNameInput;
  @ViewChild('lastNameInput') lastNameInput;
  @ViewChild('emailInput') emailInput;
  @ViewChild('passwordInput') passwordInput;
  @ViewChild('pricingMonthOptionsList') pricingMonthOptionsList;
  @ViewChild('pricingYearOptionsList') pricingYearOptionsList;
  @ViewChild('pricingOptionsDropdown') pricingOptionsDropdown;
  @ViewChild('createMyAccountButton') createMyAccountButton;

  @Select(AuthState.loading) loading$: Observable<boolean | undefined>;
  @Select(AuthState.signUpResponse) signUpResponse$: Observable<any>;
  @Select(AuthState.validateInvitationResponse) validateInvitationResponse$: Observable<any>;
  @Select(PharmacyState.pharmacies) pharmacies$: Observable<IPharmacy[]>;

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private domSanitizer: DomSanitizer,
  ) {
    this.signUpForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, CustomEmailValidator]],
      password: ['', [Validators.required, StrongPasswordValidator]],
      pricingIdMonth: [null, []],
      pricingIdYear: [null, []],
      pricingId: [null, []],
    });
    this.yearPriceHeader = this.domSanitizer.bypassSecurityTrustHtml('Yearly <span>(2 mon. FREE)</span>');
  }

  ngOnInit(): void {
    this.showForm = true;
    this.showInvitationErrors = false;
    this.userExistsError = false;
    this.invitationExpired = false;
    this.route.queryParams.subscribe((params: Params) => {
      if (params && params.username && params.code) {
        this.showForm = false;

        this.inviteData = {
          username: params.username,
          code: params.code,
        };
        this.validateInvitation();
        this.signUpForm.get('email')?.patchValue(this.inviteData.username);
      } else {
        //this.signUpForm.get('pricingId').setValidators([Validators.required]);
      }
    });
  }

  validateInvitation(): void {
    this.store.dispatch(new ValidateInvitation(this.inviteData.username, this.inviteData.code))
      .pipe(take(1), withLatestFrom(this.validateInvitationResponse$))
      .subscribe(([_, response]) => {
        if (response && response === 'Expired') {
          this.showInvitationErrors = true;
          this.invitationExpired = true;
        } else if (response && response === 'Exists') {
          this.showInvitationErrors = true;
          this.userExistsError = true;
        } else if (response && response === 'OK') {
          this.showInvitationErrors = false;
          this.showForm = true;
        }
      });
  }

  choosePricingOption(option: IPricingOption): void {
    this.signUpForm.get('pricingId').patchValue(option?.id);
    this.signUpForm.get(this.currentTab === SignUpPriceEnum.Year ? 'pricingIdMonth' : 'pricingIdYear').patchValue(null);

    this.pricingOptionsDropdown.showDropdown = false;
    if (option) {
      this.currentPricingOption = option;
      this.store.dispatch(new SetPricingOption(option.id));
    }
  }

  createAccount(): void {
   if (this.signUpForm.valid) {
      let signUpValue = this.signUpForm.getRawValue();
      signUpValue = {...signUpValue, pricingOption: PricingOption[this.currentPricingOption.id]};
      const invitedSignUp: IInvitedSignUp = {
        ...signUpValue,
        invitedEmail: this.inviteData ? this.inviteData.username : '',
        code: this.inviteData ? this.inviteData.code : '',
      };
      this.store.dispatch(
        this.inviteData ?
          new InvitedSignUp(invitedSignUp) :
          new SignUp(signUpValue)
      )
        .pipe(take(1), withLatestFrom(this.signUpResponse$))
        .subscribe(([_, response]) => {
          if (response && response.message === 'Username Taken') {
            this.signUpForm.get('email').setErrors({emailTaken: true});
          }
          if (response?.message?.includes('cognito')) {
            this.signUpForm.get('email').markAsTouched()
            this.signUpForm.get('email').setErrors({cognitoIssue: true});
          }
          if (this.inviteData) {
            this.getPharmacies();
          }
        });
    }
  }

  getPharmacies(): void {
    this.store.dispatch(new GetUserPharmacies())
      .pipe(take(1), withLatestFrom(this.pharmacies$))
      .subscribe(([_, pharmacies]) => {
        if (pharmacies.length === 1) {
          this.store.dispatch(new SetPharmacyId(pharmacies[0].id));
          this.store.dispatch(new SetCurrentPharmacy(pharmacies[0].id));
        }
      });
  }

  selectPricingOption(index: number): void {
    if (this.currentTab === SignUpPriceEnum.Month) {
      if (!this.pricingMonthOptions[index].checked) {
        this.pricingMonthOptions.forEach(el => el.checked = false);
        this.pricingYearOptions.forEach(el => el.checked = false);
        this.pricingMonthOptions[index].checked = true;
      }
    } else {
      if (!this.pricingYearOptions[index].checked) {
        this.pricingMonthOptions.forEach(el => el.checked = false);
        this.pricingYearOptions.forEach(el => el.checked = false);
        this.pricingYearOptions[index].checked = true;
      }
    }
  }

  focusInput(inputName: string): void {
    setTimeout(() => this[inputName] && this[inputName]?.appInput?.nativeElement?.focus());
  }

  focusDropdown(): void {
    setTimeout(() => {
      this.pricingOptionsDropdown.showDropdown = true;

      setTimeout(() => {
        if (this.currentTab === SignUpPriceEnum.Year) {
          this.pricingMonthOptionsList.focusedOptionIndex = 0;
          this.pricingMonthOptionsList.optionsQueryList?.toArray()[0]?.nativeElement.focus();
        } else {
          this.pricingYearOptionsList.focusedOptionIndex = 0;
          this.pricingYearOptionsList.optionsQueryList?.toArray()[0]?.nativeElement.focus();
        }
      });
    });
  }

  focusCreateMyAccount() {
    setTimeout(() => {
      this.createMyAccountButton.appButton.nativeElement.focus();
    });
  }

  tabChanged(tab: SignUpPriceEnum): void {
    this.currentTab = tab;
  }
}
